import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Form, Input, Modal } from 'antd';
import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { logout, requestPasswordCaptcha, setPassword } from '@/features/auth/api/authApi';
import { useCaptchaCountdown } from '@/features/auth/model/useCaptchaCountdown';
import { closeSetPasswordDialog, logoutSucceeded, openAuthDialog, } from '@/entities/session/model/sessionSlice';
import { clearStoredAuthToken } from '@/entities/session/model/authStorage';
import { normalizeApiError } from '@/shared/api/errors';
import { notifyApiSuccess, suppressApiErrorToast } from '@/shared/api/httpClient';
import '@/features/auth/ui/styles/auth-dialog.css';
/** 渲染设置密码表单，随弹窗关闭销毁，保证每次打开都是干净的草稿状态。 */
function SetPasswordForm() {
    const dispatch = useAppDispatch();
    const phone = useAppSelector((state) => state.session.user?.phone ?? null);
    const [form] = Form.useForm();
    const { countdown, startCountdown } = useCaptchaCountdown();
    const [errorText, setErrorText] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [sendingCaptcha, setSendingCaptcha] = useState(false);
    /** 为当前账号绑定手机号发送密码设置验证码。 */
    async function handleSendCaptcha() {
        if (!phone)
            return;
        setSendingCaptcha(true);
        setErrorText(null);
        try {
            await requestPasswordCaptcha(phone);
            startCountdown();
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSendingCaptcha(false);
        }
    }
    /** 提交验证码与新密码，成功后注销旧会话并回到未登录状态。 */
    async function handleFinish(values) {
        if (!phone)
            return;
        setSubmitting(true);
        setErrorText(null);
        try {
            await setPassword({ phone, captcha: values.captcha, password: values.password });
            // 密码已设置成功时，登出接口失败不得阻塞本地会话清理与重新登录。
            await logout().catch(() => undefined);
            clearStoredAuthToken();
            dispatch(logoutSucceeded());
            dispatch(openAuthDialog());
            notifyApiSuccess('密码设置成功，请使用新密码登录');
        }
        catch (error) {
            /** 失败文案就地展示在表单里，同步抑制统一出口，避免同一次失败弹两处。 */
            setErrorText(normalizeApiError(error).message);
            suppressApiErrorToast(error);
        }
        finally {
            setSubmitting(false);
        }
    }
    return (_jsxs(Form, { className: "auth-form", form: form, layout: "vertical", requiredMark: false, onFinish: (values) => {
            void handleFinish(values);
        }, children: [_jsx("p", { className: "auth-form__account", children: "\u9A8C\u8BC1\u5F53\u524D\u624B\u673A\u53F7\u540E\uFF0C\u53EF\u8BBE\u7F6E\u65B0\u7684\u767B\u5F55\u5BC6\u7801" }), errorText && (_jsx("p", { className: "auth-form__error", role: "alert", children: errorText })), _jsx(Form.Item, { label: "\u624B\u673A\u53F7", children: _jsx(Input, { value: phone ?? '', disabled: true, placeholder: "\u5F53\u524D\u8D26\u53F7\u672A\u7ED1\u5B9A\u624B\u673A\u53F7" }) }), !phone && (_jsx("p", { className: "auth-form__error", role: "alert", children: "\u5F53\u524D\u8D26\u53F7\u672A\u7ED1\u5B9A\u624B\u673A\u53F7\uFF0C\u65E0\u6CD5\u8BBE\u7F6E\u5BC6\u7801" })), _jsxs("div", { className: "auth-form__captcha-row", children: [_jsx(Form.Item, { className: "auth-form__captcha-field", label: "\u9A8C\u8BC1\u7801", name: "captcha", rules: [
                            { required: true, message: '请输入验证码' },
                            { pattern: /^\d{6}$/, message: '验证码为 6 位数字' },
                        ], children: _jsx(Input, { inputMode: "numeric", maxLength: 6, placeholder: "6 \u4F4D\u9A8C\u8BC1\u7801", autoComplete: "one-time-code" }) }), _jsx(Button, { className: "auth-form__captcha-button", type: "default", disabled: !phone || countdown > 0, loading: sendingCaptcha, onClick: () => {
                            void handleSendCaptcha();
                        }, children: countdown > 0 ? `${String(countdown)}s 后重发` : '获取验证码' })] }), _jsx(Form.Item, { label: "\u65B0\u5BC6\u7801", name: "password", rules: [
                    { required: true, message: '请输入新密码' },
                    { min: 6, max: 32, message: '密码长度需在 6-32 位之间' },
                ], children: _jsx(Input.Password, { placeholder: "6-32 \u4F4D\u5BC6\u7801", autoComplete: "new-password" }) }), _jsx(Form.Item, { label: "\u786E\u8BA4\u65B0\u5BC6\u7801", name: "confirmPassword", dependencies: ['password'], rules: [
                    { required: true, message: '请再次输入新密码' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value)
                                return Promise.resolve();
                            return Promise.reject(new Error('两次输入的密码不一致'));
                        },
                    }),
                ], children: _jsx(Input.Password, { placeholder: "\u8BF7\u518D\u6B21\u8F93\u5165\u65B0\u5BC6\u7801", autoComplete: "new-password" }) }), _jsx(Button, { className: "auth-form__submit", type: "primary", htmlType: "submit", block: true, disabled: !phone, loading: submitting, children: "\u786E\u8BA4\u8BBE\u7F6E" })] }));
}
/** 渲染设置密码弹窗，通过当前账号手机验证码确认用户身份。 */
export function SetPasswordDialog() {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state) => state.session.isSetPasswordDialogOpen);
    return (_jsx(Modal, { className: "auth-dialog", rootClassName: "auth-dialog-root", title: "\u8BBE\u7F6E\u5BC6\u7801", width: 480, centered: true, open: isOpen, footer: null, destroyOnHidden: true, onCancel: () => dispatch(closeSetPasswordDialog()), children: isOpen ? _jsx(SetPasswordForm, {}) : null }));
}
